import {
    EmailCampaignsListFilter,
    cloneEmailCampaignThunk,
    deleteEmailCampaignThunk,
    fetchEmailCampaignsThunk
} from '@spike/email-marketing-action';
import { EmailCampaign } from '@spike/email-marketing-model';
import DeleteModal from 'components/UI/V3/Modals/DeleteModal/DeleteModal';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { EmailCampaignDetailPath, MarketingEmailsPath } from 'routes/Paths';
import { RootState } from 'store';
import EmailCampaignsHeader from './Header/EmailCampaignsHeader';
import EmailCampaignsList from './List/EmailCampaignsList';
import EmailCampaignsEmptyState from './UI/EmailCampaignsEmptyState';
import { AddEmailCampaign } from '../UI/AddEmailCampaign/AddEmailCampaign';
import styles from './EmailCampaigns.module.scss';

const PAGE_SIZE = 10;

/**
 * Filter pill ids match `EmailCampaignsListFilter` from the lib (`"all"` plus
 * the five real backend statuses). Today the UI shows a curated subset —
 * `sending` and `failed` are intentionally omitted (the former is transient
 * and the latter is rare) until product asks for them.
 */
const filterLabels: Array<{ id: EmailCampaignsListFilter; name: string }> = [
    { id: 'all', name: 'All Campaigns' },
    { id: 'draft', name: 'Drafts' },
    { id: 'scheduled', name: 'Scheduled' },
    { id: 'sent', name: 'Sent' }
];

/**
 * Parse the optional `?editCampaign=<int>` deep-link param. We accept only
 * positive integers — anything else (or absent) returns undefined, which is
 * the "creating new" path through the wizard.
 */
const parseEditCampaignParam = (search: string): number | undefined => {
    const raw = new URLSearchParams(search).get('editCampaign');
    if (!raw) return undefined;
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const EmailCampaigns: FunctionComponent = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const apiClientWrapper = useApiClientWrapper();

    const campaigns = useSelector((state: RootState) => state.emailCampaigns.list);

    const [page, setPage] = useState<number>(1);
    const [searchText, setSearchText] = useState<string>('');
    const [selectedFilter, setSelectedFilter] = useState<EmailCampaignsListFilter>('all');
    const [campaignToDelete, setCampaignToDelete] = useState<EmailCampaign | null>(null);
    // Gates the empty-state flash on first mount and on filter change: the
    // slice's `loading` flag is false until the dispatch reaches the reducer
    // (one render later), so the list would otherwise render its empty state
    // for one frame before the spinner kicks in.
    const [initialFetchInFlight, setInitialFetchInFlight] = useState<boolean>(true);

    // Wizard state lives here (not inside the header) so both entry points
    // — the header button AND the friendly empty-state button — trigger the
    // same open handler. Mirrors the `EmailTemplates` parent ownership.
    const editCampaignFromUrl = parseEditCampaignParam(location.search);
    const [showAddEmailCampaign, setShowAddEmailCampaign] = useState<boolean>(editCampaignFromUrl !== undefined);
    const [resumingCampaignId, setResumingCampaignId] = useState<number | undefined>(editCampaignFromUrl);

    // Keep wizard state in sync if `editCampaign` changes after mount (e.g.
    // the user clicks Edit on another draft while the wizard is already open).
    useEffect(() => {
        if (editCampaignFromUrl !== undefined) {
            setResumingCampaignId(editCampaignFromUrl);
            setShowAddEmailCampaign(true);
        }
    }, [editCampaignFromUrl]);

    // Backend filters server-side via `status`. Re-fetching on filter change
    // keeps the slice scoped to what's actually being displayed, and ensures
    // a fresh draft created by the wizard shows up in the Drafts filter
    // immediately when the user switches to it.
    useEffect(() => {
        setInitialFetchInFlight(true);
        (
            dispatch(
                fetchEmailCampaignsThunk(apiClientWrapper, { status: selectedFilter })
            ) as unknown as Promise<unknown>
        ).finally(() => setInitialFetchInFlight(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilter]);

    // Search is client-side over the fetched list — matches the templates tab
    // pattern. Backend has no `q` param yet; if/when it does, lift this to a
    // server-side debounced fetch.
    const searchFilteredData = useMemo(() => {
        if (searchText.length === 0) return campaigns;
        return campaigns.filter(campaign => campaign.name.toLowerCase().includes(searchText.toLowerCase()));
    }, [campaigns, searchText]);

    const paginatedData = useMemo(
        () => searchFilteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [searchFilteredData, page]
    );

    const totalPages = useMemo(() => Math.ceil(searchFilteredData.length / PAGE_SIZE), [searchFilteredData]);

    const handleSelectFilter = (filterId: string) => {
        setSelectedFilter(filterId as EmailCampaignsListFilter);
        setPage(1);
    };

    const handleOpenNew = () => {
        setResumingCampaignId(undefined);
        setShowAddEmailCampaign(true);
    };

    const handleCloseWizard = () => {
        setShowAddEmailCampaign(false);
        setResumingCampaignId(undefined);

        // Strip `editCampaign` from the URL if it was set — otherwise refreshing
        // or sharing the URL would reopen the wizard.
        if (editCampaignFromUrl !== undefined) {
            const params = new URLSearchParams(location.search);
            params.delete('editCampaign');
            const query = params.toString();
            history.replace(`${MarketingEmailsPath}${query ? `?${query}` : ''}`);
        }
    };

    /**
     * Edit a draft → deep-link into the wizard via `?editCampaign=ID`. The
     * URL effect above watches this query param and opens the wizard
     * pre-loaded with the draft (see Resume from Drafts flow).
     *
     * The current `?tab=` value is preserved (and forced to `campaigns` to
     * match the action) so refreshing keeps the user on the right tab.
     */
    const handleEditCampaign = (campaign: EmailCampaign) => {
        if (typeof campaign.id !== 'number') return;
        const params = new URLSearchParams(location.search);
        params.set('tab', 'campaigns');
        params.set('editCampaign', String(campaign.id));
        history.push(`${MarketingEmailsPath}?${params.toString()}`);
    };

    const handleDuplicateCampaign = (campaign: EmailCampaign) => {
        if (typeof campaign.id !== 'number') return;
        dispatch(cloneEmailCampaignThunk(apiClientWrapper, campaign.id));
    };

    /**
     * Row click on a sent campaign → open the Campaign Detail view. The row
     * component itself gates this to `statusId === 'sent'`; safe to just push
     * the route here without re-checking.
     */
    const handleViewDetail = (campaign: EmailCampaign) => {
        if (typeof campaign.id !== 'number') return;
        history.push(EmailCampaignDetailPath.replace(':id', String(campaign.id)));
    };

    const handleDeleteRequest = (campaign: EmailCampaign) => {
        setCampaignToDelete(campaign);
    };

    const handleConfirmDelete = () => {
        if (!campaignToDelete || typeof campaignToDelete.id !== 'number') {
            setCampaignToDelete(null);
            return;
        }
        dispatch(deleteEmailCampaignThunk(apiClientWrapper, campaignToDelete.id));
        setCampaignToDelete(null);
    };

    const handleCancelDelete = () => {
        setCampaignToDelete(null);
    };

    // During the initial fetch we render a full-screen spinner (no header, no
    // body) so the layout never flashes through an intermediate state. Without
    // this early return the header would render in FULL mode during the fetch
    // (initialFetchInFlight=true → minimal=false), then collapse to minimal
    // once the fetch settled with zero records — a visible jump that was
    // especially loud here because campaigns has more chrome (description +
    // tabs + search) than templates does.
    if (initialFetchInFlight) {
        return (
            <div className={styles.root}>
                <div className={styles.loaderContainer}>
                    <Spinner />
                </div>

                {showAddEmailCampaign && (
                    <AddEmailCampaign
                        onClose={handleCloseWizard}
                        initialCampaignId={resumingCampaignId}
                    />
                )}
            </div>
        );
    }

    // "Truly empty" = the marketplace has never created any campaign under
    // ANY status (filter=all, no search, zero fetched records). Filter-empty
    // (e.g. no Drafts but has Sent) and search-empty scenarios stay inside
    // the list's inline `TableEmptyState` — they need the tabs + search bar
    // to remain visible so the user can back out of the empty filter.
    const isTrulyEmpty = campaigns.length === 0 && selectedFilter === 'all' && searchText.length === 0;

    return (
        <div className={styles.root}>
            <EmailCampaignsHeader
                filterOptions={filterLabels}
                searchText={searchText}
                selectedFilter={selectedFilter}
                selectedTotalRecords={searchFilteredData.length}
                onChangeSearchText={setSearchText}
                onChangeFilter={handleSelectFilter}
                onOpenNew={handleOpenNew}
                minimal={isTrulyEmpty}
            />

            {isTrulyEmpty ? (
                <EmailCampaignsEmptyState onOpenNew={handleOpenNew} />
            ) : (
                <EmailCampaignsList
                    campaigns={paginatedData}
                    hasCampaigns={searchFilteredData.length > 0}
                    page={page}
                    totalPages={totalPages}
                    onChangePage={setPage}
                    onEditCampaign={handleEditCampaign}
                    onDuplicateCampaign={handleDuplicateCampaign}
                    onDeleteCampaign={handleDeleteRequest}
                    onViewDetail={handleViewDetail}
                />
            )}

            {campaignToDelete && (
                <DeleteModal
                    title="Delete Campaign"
                    entityName={campaignToDelete.name}
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                />
            )}

            {showAddEmailCampaign && (
                <AddEmailCampaign
                    onClose={handleCloseWizard}
                    initialCampaignId={resumingCampaignId}
                />
            )}
        </div>
    );
};

export default EmailCampaigns;
